import { Link, useLocation } from "react-router-dom";
import { DISCIPLINE_THEME } from "@/lib/academyTheme";
import { useT } from "@/i18n/LanguageContext";

/* Permanent discipline switcher: Logic | Physics | Ethics.
   Skip function: always available, keeps language, never wipes progress.
   Destination is the course overview (predictable), where the prominent
   resume button carries the learner to their exact spot. */
export function DisciplineSwitcher({ active, draftGuard }) {
  const { t } = useT();
  const location = useLocation();
  const order = ["logic", "physics", "ethics"];

  const guard = (e) => {
    if (draftGuard && draftGuard()) {
      if (!window.confirm(t("academy.nav.unsavedWarning"))) e.preventDefault();
    }
  };

  return (
    <nav aria-label={t("academy.nav.switcherLabel")} data-testid="discipline-switcher"
      className="flex flex-wrap gap-2 mb-8">
      {order.map((id) => {
        const th = DISCIPLINE_THEME[id];
        const isActive = id === active;
        const label = t(`academy.nav.goTo.${id}`);
        if (isActive) {
          return (
            <span key={id} data-testid={`switcher-${id}-active`} aria-current="true"
              className={`inline-flex items-center gap-2 min-h-[44px] px-4 text-[10px] tracking-[0.22em] uppercase border ${th.activeBg} text-white/90`}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: th.accent }} aria-hidden />
              {t(`academy.nav.name.${id}`)} — {t("academy.nav.active")}
            </span>
          );
        }
        if (!th.available) {
          return (
            <span key={id} data-testid={`switcher-${id}-soon`} aria-disabled="true"
              title={t("academy.nav.comingSoon")}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 text-[10px] tracking-[0.22em] uppercase border border-white/10 text-bone/35 cursor-not-allowed">
              {t(`academy.nav.name.${id}`)} · {t("academy.nav.comingSoon")}
            </span>
          );
        }
        return (
          <Link key={id} to={th.base} onClick={guard} data-testid={`switcher-goto-${id}`}
            state={{ from: location.pathname }}
            className={`inline-flex items-center min-h-[44px] px-4 text-[10px] tracking-[0.22em] uppercase border border-white/15 text-bone/70 transition-colors hover:text-white hover:border-[var(--sw-accent)] focus:outline-none focus-visible:ring-2 ${th.ring}`}
            style={{ "--sw-accent": th.accent }}>
            {label}
          </Link>
        );
      })}
      <Link to="/academy/my-work" onClick={guard} data-testid="switcher-my-work"
        className="inline-flex items-center gap-2 min-h-[44px] px-4 text-[10px] tracking-[0.22em] uppercase border border-gold/25 text-gold/80 transition-colors hover:text-gold hover:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70">
        {t("academy.nav.myWork")}
      </Link>
    </nav>
  );
}
